'use client'

import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdSend,
    MdDelete,
    MdUpload,
    MdCheck,
    MdRefresh,
    MdWarning,
    MdAdd,
    MdSearch,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import ConfirmModal from '@/components/confirm-modal'
import {
    getEvents,
    getEvent,
    generateEvent,
    signEvent,
    signBatch,
    sendBatch,
    consultBatch,
    getCertificateStatus,
    CertificateStatus,
    EsocialEvent,
    EsocialBatch,
} from '@/lib/api/hr/hr-esocial-api'
import { getHrEmployees, HrEmployee } from '@/lib/api/hr/hr-employee-api'

const EVENT_TYPE_LABELS: Record<string, string> = {
    'S-1000': 'Info Empregador',
    'S-1010': 'Rubricas',
    'S-2200': 'Admissao',
    'S-2299': 'Desligamento',
    'S-1200': 'Remuneracao',
    'S-1299': 'Fechamento',
}

const EVENT_TYPES = ['S-1000', 'S-1010', 'S-2200', 'S-2299', 'S-1200', 'S-1299']

const EMPLOYEE_EVENT_TYPES = ['S-2200', 'S-2299', 'S-1200']
const COMPETENCE_EVENT_TYPES = ['S-1200', 'S-1299']

const STATUS_BADGES: Record<string, string> = {
    PENDING: 'badge-ghost',
    SIGNED: 'badge-info',
    SENT: 'badge-warning',
    ACCEPTED: 'badge-success',
    REJECTED: 'badge-error',
    PROCESSING: 'badge-info',
}

const STATUS_LABELS: Record<string, string> = {
    PENDING: 'Pendente',
    SIGNED: 'Assinado',
    SENT: 'Enviado',
    ACCEPTED: 'Aceito',
    REJECTED: 'Rejeitado',
    PROCESSING: 'Processando',
}

export default function EsocialPage() {
    const { user } = useAuth()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('hr', 'esocial')

    const [activeTab, setActiveTab] = useState<'certificates' | 'events' | 'batches'>('certificates')

    // ── Certificado (status do certificado fiscal) ──────────
    const [certStatus, setCertStatus] = useState<CertificateStatus | null>(null)
    const [loadingCerts, setLoadingCerts] = useState(false)

    // ── Eventos ──────────────────────────────────────────────
    const [events, setEvents] = useState<EsocialEvent[]>([])
    const [loadingEvents, setLoadingEvents] = useState(false)
    const [selectedEventIds, setSelectedEventIds] = useState<number[]>([])
    const [signingId, setSigningId] = useState<number | null>(null)
    const [signingBatch, setSigningBatch] = useState(false)
    const [sendingBatch, setSendingBatch] = useState(false)

    // Generate modal
    const generateModalRef = useRef<HTMLDialogElement>(null)
    const [genEventType, setGenEventType] = useState('S-1000')
    const [genEmployeeId, setGenEmployeeId] = useState<number | undefined>(undefined)
    const [genCompetence, setGenCompetence] = useState('')
    const [generating, setGenerating] = useState(false)
    const [employees, setEmployees] = useState<HrEmployee[]>([])

    // ── Lotes ────────────────────────────────────────────────
    const [batches, setBatches] = useState<EsocialBatch[]>([])
    const [loadingBatches, setLoadingBatches] = useState(false)
    const [consultingId, setConsultingId] = useState<number | null>(null)

    // ── Effects ──────────────────────────────────────────────

    useEffect(() => {
        loadCertificateStatus()
        loadEvents()
        loadEmployees()
    }, [])

    // ── Certificado ──────────────────────────────────────────

    const loadCertificateStatus = async () => {
        setLoadingCerts(true)
        try {
            const res = await getCertificateStatus()
            if (res.success && res.result) setCertStatus(res.result)
        } catch { /* silencioso */ }
        finally { setLoadingCerts(false) }
    }

    // ── Eventos ──────────────────────────────────────────────

    const loadEvents = async () => {
        setLoadingEvents(true)
        try {
            const res = await getEvents()
            if (res.success && res.result) {
                setEvents(res.result || [])
            } else {
                toast.error(res.error || 'Erro ao carregar eventos')
            }
        } catch {
            toast.error('Erro ao carregar eventos')
        } finally {
            setLoadingEvents(false)
        }
    }

    const loadEmployees = async () => {
        try {
            const res = await getHrEmployees({ status: 'ACTIVE', limit: 500 })
            if (res.success && res.result) {
                setEmployees(res.result || [])
            }
        } catch {
            // silently fail
        }
    }

    const openGenerateModal = () => {
        setGenEventType('S-1000')
        setGenEmployeeId(undefined)
        setGenCompetence('')
        generateModalRef.current?.showModal()
    }

    const handleGenerate = async () => {
        setGenerating(true)
        try {
            const data: any = { eventType: genEventType }
            if (EMPLOYEE_EVENT_TYPES.includes(genEventType) && genEmployeeId) {
                data.employeeId = genEmployeeId
            }
            if (COMPETENCE_EVENT_TYPES.includes(genEventType) && genCompetence) {
                data.competence = genCompetence
            }
            const res = await generateEvent(data)
            if (res.success) {
                toast.success('Evento gerado com sucesso')
                generateModalRef.current?.close()
                loadEvents()
            } else {
                toast.error(res.error || 'Erro ao gerar evento')
            }
        } catch {
            toast.error('Erro ao gerar evento')
        } finally {
            setGenerating(false)
        }
    }

    const handleSignEvent = async (id: number) => {
        setSigningId(id)
        try {
            const res = await signEvent(id)
            if (res.success) {
                toast.success('Evento assinado com sucesso')
                loadEvents()
            } else {
                toast.error(res.error || 'Erro ao assinar evento')
            }
        } catch {
            toast.error('Erro ao assinar evento')
        } finally {
            setSigningId(null)
        }
    }

    const handleSignBatch = async () => {
        if (selectedEventIds.length === 0) {
            toast.warning('Selecione ao menos um evento')
            return
        }
        setSigningBatch(true)
        try {
            const res = await signBatch(selectedEventIds)
            if (res.success) {
                toast.success('Eventos assinados com sucesso')
                setSelectedEventIds([])
                loadEvents()
            } else {
                toast.error(res.error || 'Erro ao assinar eventos')
            }
        } catch {
            toast.error('Erro ao assinar eventos')
        } finally {
            setSigningBatch(false)
        }
    }

    const handleSendBatch = async () => {
        if (selectedEventIds.length === 0) {
            toast.warning('Selecione ao menos um evento')
            return
        }
        setSendingBatch(true)
        try {
            const res = await sendBatch(selectedEventIds)
            if (res.success) {
                toast.success('Lote enviado com sucesso')
                setSelectedEventIds([])
                loadEvents()
            } else {
                toast.error(res.error || 'Erro ao enviar lote')
            }
        } catch {
            toast.error('Erro ao enviar lote')
        } finally {
            setSendingBatch(false)
        }
    }

    const toggleEventSelection = (id: number) => {
        setSelectedEventIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        )
    }

    const toggleSelectAll = () => {
        if (selectedEventIds.length === events.length) {
            setSelectedEventIds([])
        } else {
            setSelectedEventIds(events.map(e => e.id))
        }
    }

    // ── Lotes ────────────────────────────────────────────────

    const handleConsultBatch = async (batchId: number) => {
        setConsultingId(batchId)
        try {
            const res = await consultBatch(batchId)
            if (res.success) {
                toast.success('Retorno consultado com sucesso')
                loadEvents()
            } else {
                toast.error(res.error || 'Erro ao consultar retorno')
            }
        } catch {
            toast.error('Erro ao consultar retorno')
        } finally {
            setConsultingId(null)
        }
    }

    // ── Render ───────────────────────────────────────────────

    const formatDate = (d: string | null) => {
        if (!d) return '-'
        return new Date(d).toLocaleDateString('pt-BR')
    }

    const formatDateTime = (d: string | null) => {
        if (!d) return '-'
        return new Date(d).toLocaleString('pt-BR')
    }

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdSend className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            eSocial
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Gestao de eventos, certificados e lotes
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div role="tablist" className="tabs tabs-bordered mb-6">
                <button
                    role="tab"
                    className={`tab ${activeTab === 'certificates' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('certificates')}
                >
                    Certificado Digital
                </button>
                <button
                    role="tab"
                    className={`tab ${activeTab === 'events' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('events')}
                >
                    Eventos
                </button>
                <button
                    role="tab"
                    className={`tab ${activeTab === 'batches' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('batches')}
                >
                    Lotes
                </button>
            </div>

            {/* ── Tab: Certificado Digital ─────────────────── */}
            {activeTab === 'certificates' && (
                <div className="space-y-4">
                    {loadingCerts ? (
                        <div className="flex items-center justify-center min-h-[200px]">
                            <div className="loading loading-spinner loading-lg text-secondary"></div>
                        </div>
                    ) : !certStatus?.hasCertificate ? (
                        <div className="alert alert-warning">
                            <MdWarning className="text-xl" />
                            <div>
                                <p className="font-semibold">Nenhum certificado digital ativo</p>
                                <p className="text-sm">Faça o upload do certificado e-CNPJ nas <a href="/fiscal/settings" className="link link-primary">configurações fiscais</a> para assinar e enviar eventos ao eSocial.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                            <h3 className="font-semibold text-base mb-4">Certificado Digital</h3>
                            <p className="text-sm text-base-content/60 mb-4">O eSocial utiliza o mesmo certificado e-CNPJ cadastrado nas configurações fiscais.</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <span className="text-xs text-base-content/50">CNPJ</span>
                                    <p className="font-mono">{certStatus.certificate?.cnpj || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-base-content/50">Emissor</span>
                                    <p>{certStatus.certificate?.issuer || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-base-content/50">Número de Série</span>
                                    <p className="font-mono text-sm">{certStatus.certificate?.serialNumber || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-xs text-base-content/50">Validade</span>
                                    <p>
                                        {certStatus.certificate?.validFrom ? new Date(certStatus.certificate.validFrom).toLocaleDateString('pt-BR') : '-'}
                                        {' a '}
                                        {certStatus.certificate?.validUntil ? new Date(certStatus.certificate.validUntil).toLocaleDateString('pt-BR') : '-'}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4">
                                {certStatus.expired ? (
                                    <span className="badge badge-error">Expirado</span>
                                ) : (
                                    <span className="badge badge-success">Válido</span>
                                )}
                            </div>
                            {certStatus.expired && (
                                <div className="alert alert-error mt-4">
                                    <MdWarning className="text-xl" />
                                    <span>Certificado expirado. Renove nas <a href="/fiscal/settings" className="link link-primary">configurações fiscais</a>.</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Tab: Eventos ─────────────────────────────── */}
            {activeTab === 'events' && (
                <div className="space-y-4">
                    {/* Actions bar */}
                    <div className="flex flex-wrap items-center gap-2">
                        {canCreate && (
                            <button className="btn btn-secondary gap-2" onClick={openGenerateModal}>
                                <MdAdd className="text-xl" />
                                Gerar Evento
                            </button>
                        )}
                        {canUpdate && selectedEventIds.length > 0 && (
                            <>
                                <button
                                    className="btn btn-outline btn-info gap-2"
                                    onClick={handleSignBatch}
                                    disabled={signingBatch}
                                >
                                    {signingBatch ? (
                                        <span className="loading loading-spinner loading-sm"></span>
                                    ) : (
                                        <MdCheck className="text-lg" />
                                    )}
                                    Assinar Selecionados ({selectedEventIds.length})
                                </button>
                                <button
                                    className="btn btn-outline btn-warning gap-2"
                                    onClick={handleSendBatch}
                                    disabled={sendingBatch}
                                >
                                    {sendingBatch ? (
                                        <span className="loading loading-spinner loading-sm"></span>
                                    ) : (
                                        <MdSend className="text-lg" />
                                    )}
                                    Enviar Selecionados ({selectedEventIds.length})
                                </button>
                            </>
                        )}
                        <button className="btn btn-ghost btn-sm gap-1 ml-auto" onClick={loadEvents}>
                            <MdRefresh className="text-lg" />
                            Atualizar
                        </button>
                    </div>

                    {/* Events table */}
                    {loadingEvents ? (
                        <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
                            <div className="loading loading-spinner loading-lg text-secondary"></div>
                            <p className="text-base-content/60 text-sm">Carregando eventos...</p>
                        </div>
                    ) : events.length === 0 ? (
                        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                            <div className="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center">
                                <MdSend className="text-3xl text-base-content/30" />
                            </div>
                            <div className="text-center">
                                <h3 className="text-lg font-semibold text-base-content mb-1">
                                    Nenhum evento eSocial
                                </h3>
                                <p className="text-sm text-base-content/60">
                                    Gere eventos para enviar ao eSocial
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm"
                                                checked={selectedEventIds.length === events.length && events.length > 0}
                                                onChange={toggleSelectAll}
                                            />
                                        </th>
                                        <th>Tipo</th>
                                        <th>Funcionario</th>
                                        <th>Status</th>
                                        <th>Protocolo</th>
                                        <th>Data Criacao</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {events.map(ev => (
                                        <tr key={ev.id} className="hover">
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm"
                                                    checked={selectedEventIds.includes(ev.id)}
                                                    onChange={() => toggleEventSelection(ev.id)}
                                                />
                                            </td>
                                            <td>
                                                <span className="font-mono text-sm font-semibold">{ev.eventType}</span>
                                                <br />
                                                <span className="text-xs text-base-content/60">
                                                    {EVENT_TYPE_LABELS[ev.eventType] || ev.eventType}
                                                </span>
                                            </td>
                                            <td>{ev.employeeName || '-'}</td>
                                            <td>
                                                <span className={`badge ${STATUS_BADGES[ev.status] || 'badge-ghost'} badge-sm`}>
                                                    {STATUS_LABELS[ev.status] || ev.status}
                                                </span>
                                            </td>
                                            <td className="font-mono text-xs">{ev.protocol || '-'}</td>
                                            <td className="text-sm">{formatDateTime(ev.createdAt)}</td>
                                            <td>
                                                <div className="flex items-center gap-1 justify-end">
                                                    {canUpdate && ev.status === 'PENDING' && (
                                                        <button
                                                            className="btn btn-ghost btn-xs text-info"
                                                            onClick={() => handleSignEvent(ev.id)}
                                                            disabled={signingId === ev.id}
                                                            title="Assinar"
                                                        >
                                                            {signingId === ev.id ? (
                                                                <span className="loading loading-spinner loading-xs"></span>
                                                            ) : (
                                                                <MdCheck className="text-lg" />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ── Tab: Lotes ───────────────────────────────── */}
            {activeTab === 'batches' && (
                <div className="space-y-4">
                    {loadingBatches ? (
                        <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
                            <div className="loading loading-spinner loading-lg text-secondary"></div>
                            <p className="text-base-content/60 text-sm">Carregando lotes...</p>
                        </div>
                    ) : batches.length === 0 ? (
                        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                            <div className="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center">
                                <MdSend className="text-3xl text-base-content/30" />
                            </div>
                            <div className="text-center">
                                <h3 className="text-lg font-semibold text-base-content mb-1">
                                    Nenhum lote enviado
                                </h3>
                                <p className="text-sm text-base-content/60">
                                    Envie eventos para gerar lotes automaticamente
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Protocolo</th>
                                        <th>Status</th>
                                        <th>Eventos</th>
                                        <th>Ambiente</th>
                                        <th>Data Envio</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {batches.map(batch => (
                                        <tr key={batch.id} className="hover">
                                            <td className="font-mono text-sm">{batch.protocol || '-'}</td>
                                            <td>
                                                <span className={`badge ${STATUS_BADGES[batch.status] || 'badge-ghost'} badge-sm`}>
                                                    {STATUS_LABELS[batch.status] || batch.status}
                                                </span>
                                            </td>
                                            <td>{batch.eventCount}</td>
                                            <td>{batch.environment || '-'}</td>
                                            <td className="text-sm">{formatDateTime(batch.sentAt)}</td>
                                            <td>
                                                <button
                                                    className="btn btn-ghost btn-xs gap-1"
                                                    onClick={() => handleConsultBatch(batch.id)}
                                                    disabled={consultingId === batch.id}
                                                    title="Consultar Retorno"
                                                >
                                                    {consultingId === batch.id ? (
                                                        <span className="loading loading-spinner loading-xs"></span>
                                                    ) : (
                                                        <MdSearch className="text-lg" />
                                                    )}
                                                    Consultar
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ── Generate Event Modal ─────────────────────── */}
            <dialog ref={generateModalRef} className="modal">
                <div className="modal-box !max-w-lg">
                    <h3 className="font-bold text-lg mb-4">Gerar Evento eSocial</h3>

                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Tipo de Evento *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={genEventType}
                                onChange={e => setGenEventType(e.target.value)}
                            >
                                {EVENT_TYPES.map(t => (
                                    <option key={t} value={t}>{t} - {EVENT_TYPE_LABELS[t]}</option>
                                ))}
                            </select>
                        </div>

                        {EMPLOYEE_EVENT_TYPES.includes(genEventType) && (
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Funcionario</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={genEmployeeId || ''}
                                    onChange={e => setGenEmployeeId(e.target.value ? parseInt(e.target.value) : undefined)}
                                >
                                    <option value="">Selecione...</option>
                                    {employees.map(emp => (
                                        <option key={emp.id} value={emp.id}>{emp.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {COMPETENCE_EVENT_TYPES.includes(genEventType) && (
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Competencia</span>
                                </label>
                                <input
                                    type="month"
                                    className="input input-bordered w-full"
                                    value={genCompetence}
                                    onChange={e => setGenCompetence(e.target.value)}
                                />
                            </div>
                        )}
                    </div>

                    <div className="modal-action">
                        <button
                            className="btn btn-ghost"
                            onClick={() => generateModalRef.current?.close()}
                            disabled={generating}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleGenerate}
                            disabled={generating}
                        >
                            {generating && <span className="loading loading-spinner loading-sm"></span>}
                            Gerar
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={() => generateModalRef.current?.close()}>close</button>
                </form>
            </dialog>

        </div>
    )
}
